import type { CountryKey, Product, FileTransferMethod, CountryFormMap, Entry, ProductName, BaseVisaForm, VisaForm, Branch } from "~/types";
import applicantFactory from "./applicant-factory";
import generateUUID from "~/helpers/uuid";
import phoneFactory from "./phone-factory";
import { addressFactory, clientFactory, emergencyContactFactory } from "./mini-factories";
import { getDomainByBranch } from "~/helpers/domain";
import { nowTime } from "~/helpers/date";

interface Config<C extends CountryKey> {
  country: C;
  branch: Branch;
  products: Product[];
}

const FILE_TRANSFER_METHOD: FileTransferMethod = 'form';

const FACTORIES: { [C in CountryKey]: (config: Config<C>) => CountryFormMap[C] } = {
  india: indiaVisaFormFactory,
  canada: canadaVisaFormFactory,
  sri_lanka: sriLankaVisaFormFactory,
  morocco: moroccoVisaFormFactory,
  new_zealand: newZealandVisaFormFactory,
  vietnam: vietnamVisaFormFactory,
  usa: usaVisaFormFactory,
  korea: koreaVisaFormFactory,
  cambodia: cambodiaVisaFormFactory,
  israel: israelVisaFormFactory,
  saudi_arabia: saudiArabiaVisaFormFactory,
  bahrain: bahrainVisaFormFactory,
  indonesia: indonesiaVisaFormFactory,
  uk: ukVisaFormFactory,
  togo: togoVisaFormFactory,
  thailand: thailandVisaFormFactory,
  schengen: schengenVisaFormFactory
};

export default function visaFormFactory<C extends CountryKey>(config: Config<C>): CountryFormMap[C] {
  return FACTORIES[config.country](config);
}

function getBaseVisaForm<C extends CountryKey>(config: Config<C>): BaseVisaForm {
  return {
    id: generateUUID('form'),
    country: config.country,
    product: getDefaultProduct(config),
    quantity: 1,
    urgency: 'none',
    client: clientFactory(config.branch.baseCountry),
    termsAgreed: false,
    orderId: null,
    meta: metadataFactory(config.branch)
  };
}

function indiaVisaFormFactory<C extends 'india'>(config: Config<C>): CountryFormMap[C] {
  const { baseCountry } = config.branch;
  return {
    ...getBaseVisaForm(config),
    entry: entryFieldsFactory(),
    applicants: buildApplicantsArray(config),
    business: {
      name: '',
      address: addressFactory(baseCountry),
      sector: '',
      phone: phoneFactory(baseCountry),
      website: ''
    },
    fileTransferMethod: FILE_TRANSFER_METHOD
  };
}

function canadaVisaFormFactory<C extends 'canada'>(config: Config<C>): CountryFormMap[C] {
  return {
    ...getBaseVisaForm(config),
    applicants: buildApplicantsArray(config),
    entry: entryFieldsFactory()
  };
}

function sriLankaVisaFormFactory<C extends 'sri_lanka'>(config: Config<C>): CountryFormMap[C] {
  const { baseCountry } = config.branch;
  return {
    ...getBaseVisaForm(config),
    entry: entryFieldsFactory(),
    address: addressFactory(baseCountry),
    applicants: buildApplicantsArray(config),
    stayAddress: ''
  };
}

function moroccoVisaFormFactory<C extends 'morocco'>(config: Config<C>): CountryFormMap[C] {
  return {
    ...getBaseVisaForm(config),
    applicants: buildApplicantsArray(config),
    entry: entryFieldsFactory(),
    fileTransferMethod: FILE_TRANSFER_METHOD
  };
}

function newZealandVisaFormFactory<C extends 'new_zealand'>(config: Config<C>): CountryFormMap[C] {
  return {
    ...getBaseVisaForm(config),
    applicants: buildApplicantsArray(config),
    entry: entryFieldsFactory(),
    fileTransferMethod: FILE_TRANSFER_METHOD
  };
}

function vietnamVisaFormFactory<C extends 'vietnam'>(config: Config<C>): CountryFormMap[C] {
  const { baseCountry } = config.branch;
  return {
    ...getBaseVisaForm(config),
    entry: entryFieldsFactory(),
    applicants: buildApplicantsArray(config),
    emergencyContact: emergencyContactFactory(baseCountry),
    hotel: '',
    fileTransferMethod: FILE_TRANSFER_METHOD
  };
}

function usaVisaFormFactory<C extends 'usa'>(config: Config<C>): CountryFormMap[C] {
  const { baseCountry } = config.branch;
  return {
    ...getBaseVisaForm(config),
    applicants: buildApplicantsArray(config),
    emergencyContact: emergencyContactFactory(baseCountry),
    fileTransferMethod: FILE_TRANSFER_METHOD
  };
}

function koreaVisaFormFactory<C extends 'korea'>(config: Config<C>): CountryFormMap[C] {
  return {
    ...getBaseVisaForm(config),
    entry: entryFieldsFactory(),
    applicants: buildApplicantsArray(config),
    children: [],
    stayAddress: '',
    fileTransferMethod: FILE_TRANSFER_METHOD
  };
}

function cambodiaVisaFormFactory<C extends 'cambodia'>(config: Config<C>): CountryFormMap[C] {
  return {
    ...getBaseVisaForm(config),
    applicants: buildApplicantsArray(config),
    entry: entryFieldsFactory(),
    stayAddress: '',
    fileTransferMethod: FILE_TRANSFER_METHOD
  };
}

function israelVisaFormFactory<C extends 'israel'>(config: Config<C>): CountryFormMap[C] {
  return {
    ...getBaseVisaForm(config),
    applicants: buildApplicantsArray(config),
    entry: entryFieldsFactory(),
    fileTransferMethod: FILE_TRANSFER_METHOD
  };
}

function saudiArabiaVisaFormFactory<C extends 'saudi_arabia'>(config: Config<C>): CountryFormMap[C] {
  return {
    ...getBaseVisaForm(config),
    entry: entryFieldsFactory(),
    applicants: buildApplicantsArray(config),
    fileTransferMethod: FILE_TRANSFER_METHOD
  };
}

function bahrainVisaFormFactory<C extends 'bahrain'>(config: Config<C>): CountryFormMap[C] {
  return {
    ...getBaseVisaForm(config),
    applicants: buildApplicantsArray(config),
    fileTransferMethod: FILE_TRANSFER_METHOD
  };
}

function indonesiaVisaFormFactory<C extends 'indonesia'>(config: Config<C>): CountryFormMap[C] {
  return {
    ...getBaseVisaForm(config),
    applicants: buildApplicantsArray(config),
    stayAddress: '',
    fileTransferMethod: FILE_TRANSFER_METHOD
  };
}

function ukVisaFormFactory<C extends 'uk'>(config: Config<C>): CountryFormMap[C] {
  return {
    ...getBaseVisaForm(config),
    applicants: buildApplicantsArray(config),
    fileTransferMethod: FILE_TRANSFER_METHOD
  };
}

function togoVisaFormFactory<C extends 'togo'>(config: Config<C>): CountryFormMap[C] {
  const { baseCountry } = config.branch;
  return {
    ...getBaseVisaForm(config),
    applicants: buildApplicantsArray(config),
    entry: entryFieldsFactory(),
    stayAddress: '',
    emergencyContact: emergencyContactFactory(baseCountry),
    fileTransferMethod: FILE_TRANSFER_METHOD
  };
}

function thailandVisaFormFactory<C extends 'thailand'>(config: Config<C>): CountryFormMap[C] {
  return {
    ...getBaseVisaForm(config),
    applicants: buildApplicantsArray(config),
    entry: entryFieldsFactory({ hasCrossing: true }),
    stayAddress: '',
    fileTransferMethod: FILE_TRANSFER_METHOD
  };
}

function schengenVisaFormFactory<C extends 'schengen'>(config: Config<C>): CountryFormMap[C] {
  return {
    ...getBaseVisaForm(config),
    applicants: buildApplicantsArray(config),
    entry: entryFieldsFactory(),
    stayAddress: ''
  };
}

function productNameFactory(country: CountryKey): ProductName {
  switch (country) {
    case 'india': return 'india_tourist_month';
    case 'canada': return 'canada_5_years';
    case 'morocco': return 'morocco_tourist_6_months';
    case 'sri_lanka': return 'sri_lanka_month';
    case 'vietnam': return 'vietnam_3_months_single';
    case 'new_zealand': return 'new_zealand_2_years';
    case 'usa': return 'usa_2_years';
    case 'korea': return 'korea_3_years';
    case 'cambodia': return 'cambodia_month';
    case 'israel': return 'israel_2_years';
    case 'saudi_arabia': return 'saudi_arabia_year';
    case 'bahrain': return 'bahrain_2_weeks';
    case 'indonesia': return 'indonesia_2_months';
    case 'uk': return 'uk_2_years';
    case 'togo': return 'togo_15_days_single';
    case 'thailand': return 'thailand_5_years';
    case 'schengen': return 'schengen';
  }
}

function entryFieldsFactory(options: { hasCrossing?: boolean } = {}): Entry {
  return {
    date: '',
    port: null,
    crossing: options.hasCrossing ? { type: 'air', flight: '' } : undefined
  };
}

function getDefaultProduct(config: Config<CountryKey>) {
  const { country, products, branch } = config;
  const name = productNameFactory(country);
  const replacement = branch.productReplacement?.[name];
  const product = products.find(replacement
    ? p => p.name == replacement
    : p => p.name == name
  );
  if (!product) throw new Error('Failed finding default product');
  return product;
}

function buildApplicantsArray<C extends CountryKey>({ country, branch }: Config<C>) {
  return [applicantFactory({ country, branch })];
}

function metadataFactory(branch: Branch): VisaForm['meta'] {
  return {
    branch: branch.code,
    domain: getDomainByBranch(branch.code),
    url: '',
    referrer: '',
    step: 1,
    completion: 0,
    resumed: false,
    timestamp: nowTime(),
    clientIp: '',
    revision: 0
  };
}